import { fail } from "./types";
export type DateValue = {
  raw: string;
  normalized: string;
  precision: "day" | "instant";
  timezone: string | null;
  epoch?: number;
};
const months = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];
function validDay(y: number, m: number, d: number) {
  return (
    y >= 1000 &&
    y <= 9999 &&
    m >= 1 &&
    m <= 12 &&
    d >= 1 &&
    d <= new Date(Date.UTC(y, m, 0)).getUTCDate()
  );
}
export function dateValue(
  raw: unknown,
  format: "iso" | "fr" = "iso",
): DateValue {
  if (typeof raw !== "string")
    fail("non_determine", "Date non textuelle ou absente.");
  const s = (raw as string).trim();
  let match = s.match(/^(\d{4})-(\d{2})-(\d{2})(.*)$/);
  let y: number,
    m: number,
    d: number,
    tail = "";
  if (match) {
    y = +match[1];
    m = +match[2];
    d = +match[3];
    tail = match[4];
  } else if (
    format === "fr" &&
    (match = s
      .toLowerCase()
      .match(
        /^(\d{1,2}|1er) (janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre) (\d{4})$/,
      ))
  ) {
    y = +match![3];
    m = months.indexOf(match![2]) + 1;
    d = parseInt(match![1]);
  } else
    fail(
      "non_determine",
      "Format de date ambigu ou hors du format déclaré (ISO / jour mois français année).",
    );
  if (!validDay(y!, m!, d!)) fail("invalide", `Date impossible : ${s}.`);
  const day = `${y!}-${String(m!).padStart(2, "0")}-${String(d!).padStart(2, "0")}`;
  if (!tail)
    return { raw: s, normalized: day, precision: "day", timezone: null };
  const t = tail.match(
    /^T(\d{2}):(\d{2}):(\d{2})(\.\d{1,3})?(Z|[+-](\d{2}):(\d{2}))$/,
  );
  if (!t)
    fail(
      "non_determine",
      "Horodatage ISO attendu avec secondes et fuseau explicite.",
    );
  if (
    +t![1] > 23 ||
    +t![2] > 59 ||
    +t![3] > 59 ||
    +(t![6] || 0) > 14 ||
    +(t![7] || 0) > 59 ||
    (+t![6] === 14 && +t![7] !== 0)
  )
    fail("invalide", "Heure ou décalage impossible.");
  const epoch = Date.parse(s);
  if (!Number.isFinite(epoch)) fail("invalide", "Horodatage invalide.");
  return {
    raw: s,
    normalized: new Date(epoch).toISOString(),
    precision: "instant",
    timezone: t![5],
    epoch,
  };
}
export function dayIn(d: DateValue, zone?: string): string {
  if (d.precision === "day") return d.normalized;
  if (!zone)
    fail(
      "non_determine",
      "Fuseau du profil nécessaire pour comparer un jour et un horodatage.",
    );
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d.epoch);
    return ["year", "month", "day"]
      .map((k) => parts.find((p) => p.type === k)!.value)
      .join("-");
  } catch {
    return fail("invalide", "Fuseau IANA inconnu.");
  }
}
export function compareDates(a: DateValue, b: DateValue, zone?: string) {
  return a.precision === "instant" && b.precision === "instant"
    ? a.epoch === b.epoch
    : dayIn(a, zone) === dayIn(b, zone);
}
export function compareOrder(a: DateValue, b: DateValue, zone?: string) {
  const av =
    a.precision === "instant" && b.precision === "instant"
      ? a.epoch!
      : dayIn(a, zone);
  const bv =
    a.precision === "instant" && b.precision === "instant"
      ? b.epoch!
      : dayIn(b, zone);
  return av < bv ? -1 : av > bv ? 1 : 0;
}
